/**
 * Chat API Service
 * Handles all chat-related API endpoints
 * All endpoints use VITE_LLM_URL
 */

import apiClient from "./client";

// LLM API Base URL - required for all chat operations
const LLM_BASE_URL = import.meta.env.VITE_LLM_URL;

if (!LLM_BASE_URL) {
  console.error("VITE_LLM_URL is not configured in environment variables");
}

// Common headers for all LLM API requests
const LLM_HEADERS = {
  "X-App-Source": "extension",
};

export interface Chat {
  chat_id: string;
  chat_name: string;
  source: string;
  created_at: string;
}

export interface PaginatedChatsResponse {
  page: number;
  page_size: number;
  total_chats: number;
  total_pages: number;
  chats: Chat[];
}

export interface Message {
  role: "user" | "assistant" | "system";
  content: string;
  timestamp?: string;
  id?: string;
}

export interface ChatHistoryResponse {
  chat_id: string;
  title: string;
  messages: Message[];
  created_at: string;
}

export interface StartChatResponse {
  chat_id: string;
  source: string;
  created_at: string;
}

export class ChatService {
  /**
   * Start a new chat session
   * Extension format: POST /start_chat with empty payload {}
   * Backend creates a new chat session and returns chat_id
   */
  static async startChat(): Promise<StartChatResponse> {
    return apiClient.post<StartChatResponse>(
      "/start_chat",
      {},
      { baseUrl: LLM_BASE_URL, headers: LLM_HEADERS },
    );
  }

  /**
   * Get list of user's chats with pagination
   */
  static async getUserChats(
    page: number = 1,
    pageSize: number = 10,
  ): Promise<Chat[]> {
    const response = await apiClient.get<PaginatedChatsResponse>(
      `/user_chats?page=${page}&page_size=${pageSize}`,
      { baseUrl: LLM_BASE_URL, headers: LLM_HEADERS },
    );
    return response.chats || [];
  }

  /**
   * Get chat history with messages
   * Extension format: POST /chat_history with pagination
   */
  static async getChatHistory(
    chatId: string,
    page: number = 1,
    pageSize: number = 10,
  ): Promise<ChatHistoryResponse> {
    return apiClient.post<ChatHistoryResponse>(
      "/chat_history",
      { chat_id: chatId, page, page_size: pageSize },
      { baseUrl: LLM_BASE_URL, headers: LLM_HEADERS },
    );
  }

  /**
   * Delete a chat
   */
  static async deleteChat(
    chatId: string,
  ): Promise<{ success: boolean; message?: string; error?: string }> {
    return apiClient.delete(`/delete_chat/${chatId}`, {
      baseUrl: LLM_BASE_URL,
      headers: LLM_HEADERS,
    });
  }

  /**
   * Rename a chat
   */
  static async renameChat(
    chatId: string,
    newTitle: string,
  ): Promise<{ success: boolean; message: string; new_name: string }> {
    return apiClient.put(
      "/rename_chat",
      { chat_id: chatId, new_title: newTitle },
      { baseUrl: LLM_BASE_URL, headers: LLM_HEADERS },
    );
  }

  /**
   * Submit message review (like/dislike)
   * Extension format: PUT /message_review with like/dislike booleans
   */
  static async reviewMessage(
    chatId: string,
    messageId: string,
    review: "like" | "dislike",
  ): Promise<{ message: string }> {
    return apiClient.put(
      "/message_review",
      {
        chat_id: chatId,
        message_id: messageId,
        like: review === "like",
        dislike: review === "dislike",
      },
      { baseUrl: LLM_BASE_URL, headers: LLM_HEADERS },
    );
  }

  /**
   * Get total chat count
   */
  static async getTotalChats(): Promise<number> {
    const response = await apiClient.get<{ total: number }>("/total_chats");
    return response.total || 0;
  }
}
